import React from 'react';
import { Box, Typography, Chip, useTheme, Divider } from '@mui/material';
import { AccessTime, Person, CalendarToday, Visibility } from '@mui/icons-material';

interface BlogPreviewProps {
  title: string;
  summary: string;
  content: string;
  coverImage?: string;
  imageAlt?: string;
  author?: string;
  publishedAt?: string;
  readingTime?: number;
  keywords?: string[];
  views?: number;
  mode?: 'light' | 'dark';
}

const BlogPreview: React.FC<BlogPreviewProps> = ({
  title,
  summary,
  content,
  coverImage,
  imageAlt,
  author,
  publishedAt,
  readingTime,
  keywords,
  views,
  mode = 'light',
}) => {
  const theme = useTheme();

  const formatDate = (dateString?: string) => {
    if (!dateString) return new Date().toLocaleDateString('cs-CZ');
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const previewStyles = {
    light: {
      background: '#ffffff',
      text: '#1a1a1a',
      textSecondary: '#666666',
      border: '#e0e0e0',
    },
    dark: {
      background: theme.palette.background.alt,
      text: theme.palette.secondary[100],
      textSecondary: theme.palette.secondary[300],
      border: theme.palette.secondary[500],
    },
  };

  const styles = previewStyles[mode];

  return (
    <Box
      sx={{
        backgroundColor: styles.background,
        borderRadius: '12px',
        overflow: 'hidden',
        maxWidth: '800px',
        margin: '0 auto',
        boxShadow: mode === 'light' ? '0 2px 12px rgba(0,0,0,0.1)' : 'none',
      }}
    >
      {/* Cover Image */}
      {coverImage && (
        <Box
          component="img"
          src={
            coverImage.startsWith('http')
              ? coverImage
              : `https://smartpush-aws-s3-bucket.s3.eu-central-1.amazonaws.com/${coverImage}`
          }
          alt={imageAlt || title}
          sx={{
            width: '100%',
            height: '400px',
            objectFit: 'cover',
          }}
        />
      )}

      <Box p="2rem">
        {/* Keywords */}
        {keywords && keywords.length > 0 && (
          <Box display="flex" gap="0.5rem" flexWrap="wrap" mb="1rem">
            {keywords.map((keyword) => (
              <Chip
                key={keyword}
                label={keyword}
                size="small"
                sx={{
                  backgroundColor: mode === 'light' ? '#f0f0f0' : theme.palette.primary.light,
                  color: styles.textSecondary,
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </Box>
        )}

        {/* Title */}
        <Typography
          variant="h3"
          component="h1"
          sx={{
            color: styles.text,
            fontWeight: 700,
            mb: '1rem',
            lineHeight: 1.3,
          }}
        >
          {title || 'Název příspěvku'}
        </Typography>

        {/* Meta info */}
        <Box
          display="flex"
          alignItems="center"
          gap="1.5rem"
          flexWrap="wrap"
          mb="1.5rem"
          sx={{ color: styles.textSecondary }}
        >
          {author && (
            <Box display="flex" alignItems="center" gap="0.5rem">
              <Person fontSize="small" />
              <Typography variant="body2">{author}</Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" gap="0.5rem">
            <CalendarToday fontSize="small" />
            <Typography variant="body2">{formatDate(publishedAt)}</Typography>
          </Box>
          {readingTime && (
            <Box display="flex" alignItems="center" gap="0.5rem">
              <AccessTime fontSize="small" />
              <Typography variant="body2">{readingTime} min čtení</Typography>
            </Box>
          )}
          {views !== undefined && (
            <Box display="flex" alignItems="center" gap="0.5rem">
              <Visibility fontSize="small" />
              <Typography variant="body2">{views} zobrazení</Typography>
            </Box>
          )}
        </Box>

        {/* Summary */}
        {summary && (
          <Typography
            variant="h6"
            sx={{
              color: styles.textSecondary,
              fontWeight: 400,
              fontStyle: 'italic',
              mb: '2rem',
              lineHeight: 1.6,
            }}
          >
            {summary}
          </Typography>
        )}

        <Divider sx={{ mb: '2rem', borderColor: styles.border }} />

        {/* Content */}
        <Box
          className="blog-content-preview"
          dangerouslySetInnerHTML={{ __html: content || '<p>Obsah příspěvku...</p>' }}
          sx={{
            color: styles.text,
            lineHeight: 1.8,
            fontSize: '1.1rem',
            '& h1': {
              fontSize: '2rem',
              fontWeight: 700,
              mt: '2rem',
              mb: '1rem',
              color: styles.text,
            },
            '& h2': {
              fontSize: '1.6rem',
              fontWeight: 600,
              mt: '1.5rem',
              mb: '0.75rem',
              color: styles.text,
            },
            '& h3': {
              fontSize: '1.3rem',
              fontWeight: 600,
              mt: '1.25rem',
              mb: '0.5rem',
              color: styles.text,
            },
            '& p': {
              mb: '1rem',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '8px',
              my: '1.5rem',
              display: 'block',
            },
            '& a': {
              color: '#1976d2',
              textDecoration: 'underline',
            },
            '& ul, & ol': {
              pl: '1.5rem',
              mb: '1rem',
            },
            '& li': {
              mb: '0.5rem',
            },
            '& blockquote': {
              borderLeft: '4px solid #ccc',
              pl: '1rem',
              ml: 0,
              fontStyle: 'italic',
              color: styles.textSecondary,
              my: '1.5rem',
            },
            '& pre': {
              backgroundColor: mode === 'light' ? '#f5f5f5' : theme.palette.primary.light,
              p: '1rem',
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '0.9rem',
            },
            '& code': {
              backgroundColor: mode === 'light' ? '#f0f0f0' : theme.palette.primary.light,
              px: '0.25rem',
              borderRadius: '4px',
              fontSize: '0.9rem',
            },
            '& iframe': {
              maxWidth: '100%',
              my: '1.5rem',
              borderRadius: '8px',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default BlogPreview;
